import { FC, PropsWithChildren, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { DefaultError, UseMutationResult } from '@tanstack/react-query';
import { CircleCheck, CircleX } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { ConfigureApiTokenIntegrationParams } from '@/modules/onboarding/integrations-setup/types/api-token/configure-api-token-integration-params';
import { UseConfigureApiTokenIntegrationParams } from '@/modules/onboarding/integrations-setup/types/api-token/use-configure-api-token-integration-params';

import { guideMap } from '../../helpers/data';

const createApiTokenConnectionSchema = (t: (key: string) => string) =>
  z.object({
    apiToken: z.string().min(1, t('fields.apiToken.validation.required')),
  });

type ApiTokenConnectionFormValues = z.infer<ReturnType<typeof createApiTokenConnectionSchema>>;

interface ConnectionApiTokenModalProps extends PropsWithChildren {
  service: IntegrationServices;
  /** Controlled open state (optional) */
  open?: boolean;
  /** Callback when open state changes (optional) */
  onOpenChange?: (open: boolean) => void;

  useConfigureApiTokenIntegration: (
    params: UseConfigureApiTokenIntegrationParams
  ) => UseMutationResult<unknown, DefaultError, ConfigureApiTokenIntegrationParams>;
}

export const ConnectionApiTokenModal: FC<ConnectionApiTokenModalProps> = ({
  service,
  children,
  open: controlledOpen,
  onOpenChange,
  useConfigureApiTokenIntegration,
}) => {
  const { t } = useTranslation(['authenticated.onboarding.integrations']);

  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const connectionFormSchema = createApiTokenConnectionSchema(t);
  const form = useForm<ApiTokenConnectionFormValues>({
    resolver: zodResolver(connectionFormSchema),
    defaultValues: {
      apiToken: '',
    },
  });

  const { mutateAsync, isPending } = useConfigureApiTokenIntegration({
    onError: error => {
      form.setError('root', {
        type: 'server',
        message: error.message ?? t('toast.integrationError'),
      });
    },
  });

  const handleSubmit = async (values: ApiTokenConnectionFormValues) => {
    await mutateAsync(values);
  };

  const handleServiceGuide = () => {
    const guideLink = guideMap[service];
    window.open(guideLink, '_blank')?.focus();
  };

  const handleClose = (open: boolean) => {
    form.reset();
    setOpen(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-2">
          <DialogTitle className="mb-2">
            {t('modals.connection.connectServiceTemplate', { serviceName: service })}
          </DialogTitle>
        </DialogHeader>

        {form.formState.isSubmitSuccessful && (
          <Alert variant="success">
            <div className="flex items-center gap-1.5">
              <CircleCheck className="h-4 w-4 flex-shrink-0" />

              <AlertDescription className="text-sm/4 font-medium">
                {t('toast.successConnect')}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {form.formState.errors.root && (
          <Alert variant="error">
            <div className="flex items-center gap-1.5">
              <CircleX className="h-4 w-4 flex-shrink-0" />

              <AlertDescription className="text-sm/4 font-medium">
                {t('toast.errorConnect')}
              </AlertDescription>
            </div>
          </Alert>
        )}

        <div className="rounded-lg border border-input bg-primary-foreground p-4 text-xs text-foreground [&>svg]:text-foreground">
          <div className="flex items-center justify-between gap-1.5">
            {t('modals.connection.alert.serviceGuideTemplate', { serviceName: service })}
            <Button type="button" variant="link" size="sm" onClick={handleServiceGuide}>
              {t('modals.connection.alert.openGuide')}
            </Button>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="apiToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.apiToken.label')}</FormLabel>

                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t('fields.apiToken.placeholder')}
                      {...field}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4">
              {form.formState.isSubmitSuccessful ? (
                <DialogClose asChild>
                  <Button type="button">{t('actions.close')}</Button>
                </DialogClose>
              ) : (
                <>
                  <DialogClose asChild>
                    <Button type="button" variant="outline">
                      {t('actions.close')}
                    </Button>
                  </DialogClose>

                  <ButtonLoadable isLoading={isPending}>
                    {isPending
                      ? t('modals.connection.actions.connecting')
                      : t('modals.connection.actions.testConnection')}
                  </ButtonLoadable>
                </>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
